import { isNumber, isString } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCustomEventEmitter } from '@/hook';
import { uuid } from '@/util';

import type { VTreeRef } from '../../VTree';
import VFileBrowserEmptyContentList from '../VFileBrowserEmptyContentList';
import VFileBrowserEmptyTree from '../VFileBrowserEmptyTree';
import { DEFAULT_COLUMN_LIST } from '../constant';
import type { ItemNodeList } from '../model';
import createItemNodeList from '../model';
import type {
  Column,
  ExpandedListItem,
  ListItem,
  VFileBrowserContextValue,
  VFileBrowserProps,
} from '../type';
import { doAnyItemList as _doAnyItemList } from '../util';
import FileBrowserContext from './fileBrowserContext';

const VFileBrowserContextProvider = <T extends Dictionary = {}>({
  children,
  emptyRenderer: _emptyRenderer,
  ...rest
}: React.PropsWithChildren<VFileBrowserProps<T>>) => {
  const rootItemRef = useRef({
    name: '',
    isDir: true,
    parentUuidList: [],
    uuid: 'root',
    children: [],
  } as unknown as ExpandedListItem<T>);
  const vTreeRef = useRef<VTreeRef>(null);
  const [currentDirUuid, setCurrentDirUuid] = useState<Nullish<string>>(
    rootItemRef.current.uuid,
  );
  const emptyRenderer = useMemo(() => {
    const { content, contentList, tree } = _emptyRenderer ?? {};

    return {
      content:
        content ?? contentList ?? (() => <VFileBrowserEmptyContentList />),
      tree: tree ?? (() => <VFileBrowserEmptyTree />),
    };
  }, [_emptyRenderer]);
  const [selectedItemUuidList, setSelectedItemUuidList] = useState<string[]>(
    [],
  );
  const [searchText, setSearchText] = useState<string>('');
  const [_itemList, _setItemList] = useState(rest.itemList);
  const [itemByUuid, itemListWithUuid] = useMemo(() => {
    const itemByUuid = {
      [rootItemRef.current.uuid]: rootItemRef.current,
    } as Dictionary<ExpandedListItem<T>>;

    const withUuid = (item: ListItem): ExpandedListItem<T> => {
      const _uuid = item.uuid ?? uuid();

      itemByUuid[_uuid] = {
        ...item,
        children: item.children?.map(withUuid),
        get uuid() {
          return _uuid;
        },
      } as ExpandedListItem<T>;

      return itemByUuid[_uuid];
    };

    rootItemRef.current.children = _itemList.map(item => withUuid(item));

    return [itemByUuid, rootItemRef.current.children];
  }, [_itemList]);
  const doAnyItemList = _doAnyItemList(itemByUuid);
  const currentDir = useMemo(
    () => (currentDirUuid ? itemByUuid[currentDirUuid] : null),
    [currentDirUuid, itemByUuid],
  );
  const selectedItemNodeList = useMemo(
    () =>
      createItemNodeList(
        selectedItemUuidList.length
          ? selectedItemUuidList.map(uuid => itemByUuid[uuid])
          : [],
      ),
    [selectedItemUuidList, itemByUuid],
  );
  const filteredItemList = useMemo(() => {
    const itemList = Object.values(itemByUuid);

    return !searchText
      ? []
      : itemList.filter(item => {
          return Object.values(item)
            .filter(value => isString(value) || isNumber(value))
            .some(value => `${value}`.match(searchText));
        });
  }, [itemByUuid, searchText]);

  const isSearching = !!searchText;

  const contentItemNodeList = useMemo(() => {
    return createItemNodeList(
      (isSearching
        ? filteredItemList
        : (currentDir?.children ?? itemListWithUuid)
      ).toSorted((a, b) => {
        const [v1, v2] = [a.isDir ? 1 : 0, b.isDir ? 1 : 0];

        return v2 - v1;
      }),
    ) as ItemNodeList<ExpandedListItem<T>>;
  }, [isSearching, filteredItemList, currentDir?.children, itemListWithUuid]);

  const columnList = useMemo(() => {
    if (!(rest.columnList instanceof Array)) return DEFAULT_COLUMN_LIST;

    const columnList = DEFAULT_COLUMN_LIST as unknown as Column<T>[];

    rest.columnList.forEach(column => {
      const { name } = column;
      const findedDefaultColumnIndex = columnList.findIndex(
        c => c.name === name,
      );

      if (findedDefaultColumnIndex >= 0) {
        columnList[findedDefaultColumnIndex] = column;
      } else {
        columnList.push(column);
      }
    });

    return columnList;
  }, [rest.columnList]);

  const { eventEmitter } = useCustomEventEmitter({
    onCloseDirectory: rest.onCloseDirectory ?? (() => {}),
    onEndOfDirectory: rest.onEndOfDirectory ?? (() => {}),
    onItemClick: rest.onItemClick ?? (() => {}),
    onItemDblClick: rest.onItemDblClick ?? (() => {}),
    onItemDownload: rest.onItemDownload ?? (() => {}),
    onItemRemove: rest.onItemRemove ?? (() => {}),
    onItemSelect: rest.onItemSelect ?? (() => {}),
    onOpenDirectory: rest.onOpenDirectory ?? (() => {}),
    onReload: rest.onReload ?? (() => {}),
  });

  const lastSelectedItemUuid =
    selectedItemUuidList[selectedItemUuidList.length - 1];

  const selectItem = (selectedItem: ExpandedListItem<T> | string) =>
    selectItemList([selectedItem]);

  const selectItemList = (
    selectedItemList: (ExpandedListItem<T> | string)[],
  ) => {
    setSelectedItemUuidList(
      selectedItemList.map(i => (typeof i === 'string' ? i : i.uuid)),
    );

    eventEmitter.current.emit('itemSelect', {
      itemList: selectedItemList.map(i =>
        typeof i === 'string' ? itemByUuid[i] : i,
      ),
    });
  };

  const downloadItemList = useCallback(
    (itemList: (ExpandedListItem<T> | string)[]) => {
      doAnyItemList(itemList, itemList => {
        if (itemList.length <= 0) return;

        eventEmitter.current.emit('itemDownload', { itemList });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemByUuid],
  );

  const removeItemList = useCallback(
    (itemList: (ExpandedListItem<T> | string)[]) => {
      doAnyItemList(itemList, itemList => {
        if (itemList.length <= 0) return;

        eventEmitter.current.emit('itemRemove', { itemList });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemByUuid],
  );

  const isVisibleColumn = useCallback(
    <N extends Exclude<keyof ListItem, symbol>>(name: N) => {
      return !columnList.find(column => column.name === name)?.hidden;
    },
    [columnList],
  );

  const setChildren: VFileBrowserContextValue<T>['setChildren'] = (
    itemOrItemUuid,
    children,
  ) => {
    const uuid =
      typeof itemOrItemUuid === 'string'
        ? itemOrItemUuid
        : (itemOrItemUuid as ExpandedListItem<T>).uuid;

    const item = itemByUuid[uuid];

    if (item == null) {
      console.warn(`Not found Item(${uuid})`);

      return;
    }

    (item as ListItem).children = children;
  };

  useEffect(() => {
    _setItemList(rest.itemList);
  }, [rest.itemList]);

  return (
    <FileBrowserContext.Provider<VFileBrowserContextValue<T>>
      value={{
        ...rest,
        columnList,
        contentItemNodeList,

        currentDir,
        currentDirUuid,

        emptyRenderer,
        eventEmitter: eventEmitter.current,

        filteredItemList,

        isSearching,

        itemByUuid,
        itemList: itemListWithUuid,

        lastSelectedItemUuid,

        rootItemRef,

        vTreeRef,

        searchText,
        selectedItemUuidList,
        selectedItemNodeList,

        downloadItemList,

        isVisibleColumn,

        removeItemList,

        selectItem,
        selectItemList,

        setChildren,
        setCurrentDirUuid,
        setSearchText,
      }}
    >
      {children}
    </FileBrowserContext.Provider>
  );
};

export default VFileBrowserContextProvider;
